// REST client for the lock surface per docs/apis/operator-bff-tds.md §5.
// BFF endpoints land with NEX-70; for now operator-ui hits them through MSW
// in dev (and against the real BFF when NEX-70 ships).

import { HTTPError } from 'ky';
import { http } from '@/lib/http';
import { LockConflictError, type AcquireLockResponse, type LockHolder, type LockState } from './types';

function lockPath(entityUrn: string): string {
  // URN-encoded per docs/tech-stack.md §1.8.
  return `v1/locks/${encodeURIComponent(entityUrn)}`;
}

// The BFF's auto-lock-on-write 409 body (operator-bff sub-case-lock.ts):
//   { error: 'locked', entityUrn, holder: { operatorUrn, displayName } | null, expiresAt }
// A write path (select / book / reassign …) that hits lock contention gets THIS,
// not the lock-state body the acquire endpoint returns. `asLockConflict` turns
// any such 409 into a `LockConflictError` so every write surfaces a consistent
// "PNR x is being edited by <holder>" message; non-lock errors pass through.
interface ILockContention409 {
  error?: string;
  entityUrn?: string;
  holder?: LockHolder | null;
  expiresAt?: string | null;
}

export async function asLockConflict(error: unknown): Promise<never> {
  if (error instanceof HTTPError && error.response.status === 409) {
    try {
      const body = (await error.response.clone().json()) as ILockContention409;
      if (body?.error === 'locked') {
        throw new LockConflictError({
          entityUrn: body.entityUrn ?? '',
          holder: body.holder ?? null,
          expiresAt: body.expiresAt ?? null,
        });
      }
    } catch (parsed) {
      if (parsed instanceof LockConflictError) throw parsed;
      // fall through to rethrow the original error
    }
  }
  throw error;
}

export async function getLockState(entityUrn: string): Promise<LockState> {
  return http.get(lockPath(entityUrn)).json<LockState>();
}

export async function acquireLock(entityUrn: string): Promise<LockState> {
  try {
    const res = await http.post(lockPath(entityUrn)).json<AcquireLockResponse>();
    return res.state;
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 409) {
      const conflict = (await err.response.json()) as LockState;
      throw new LockConflictError(conflict);
    }
    throw err;
  }
}

export async function heartbeatLock(entityUrn: string): Promise<LockState | null> {
  // PUT mirrors NestJS conventions for "refresh existing resource state". The
  // BFF returns the refreshed { state } as JSON; tolerate an empty body (a
  // legacy 204) by returning null so the caller skips the cache write.
  const res = await http.put(lockPath(entityUrn));
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  return (JSON.parse(text) as AcquireLockResponse).state;
}

export async function releaseLock(entityUrn: string): Promise<void> {
  await http.delete(lockPath(entityUrn));
}
